import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { seoAPI } from '../../../config/seo-api';
import { helpers } from '../../../utils/helpers';
import { slugifyCategory } from '../../../utils/seoHelpers';
import {
  buildCategorySeoPreviewUrl,
  buildCategorySeoSavePayload,
  calculateCategorySeoScore,
  createEmptyContentSection,
  getSeoStatusClass,
  getSeoStatusFromScore,
  initializeCategorySeoFormData,
} from '../../../utils/categorySeoHelpers';

const META_TITLE_MAX = 70;
const META_DESCRIPTION_MAX = 160;

const EditCategorySeoModal = ({ item, adminData, onClose, onSaved }) => {
  const canEdit = helpers.hasSeoEditAccess(adminData);

  const [activeTab, setActiveTab] = useState('basic');
  const [categoryData, setCategoryData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCategory = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const response = await seoAPI.fetchSeoCategory(item._id, item.subCategoryId);

        if (response.success) {
          setCategoryData(response.data);
          setFormData(initializeCategorySeoFormData(response.data));
        } else {
          setLoadError(response.msg || response.error || 'Failed to load category SEO data');
        }
      } catch (error) {
        console.error('Category SEO fetch error:', error);
        setLoadError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [item._id, item.subCategoryId]);

  const previewUrl = useMemo(() => {
    if (!categoryData || !formData) return '';
    return buildCategorySeoPreviewUrl(categoryData, formData.urlSlug);
  }, [categoryData, formData]);

  const liveScore = useMemo(() => {
    if (!formData) return 0;
    return calculateCategorySeoScore(formData);
  }, [formData]);

  const statusForDisplay = getSeoStatusFromScore(liveScore);
  const statusClass = getSeoStatusClass(statusForDisplay);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSectionChange = (index, field, value) => {
    setFormData((prev) => {
      const contentSections = [...prev.contentSections];
      contentSections[index] = { ...contentSections[index], [field]: value };
      return { ...prev, contentSections };
    });
  };

  const handleAddSection = () => {
    setFormData((prev) => ({
      ...prev,
      contentSections: [...prev.contentSections, createEmptyContentSection()],
    }));
  };

  const handleRemoveSection = (index) => {
    setFormData((prev) => ({
      ...prev,
      contentSections: prev.contentSections.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    if (!canEdit || !categoryData || !formData) return;

    try {
      setSaving(true);
      const payload = buildCategorySeoSavePayload(categoryData, formData);
      const response = await seoAPI.saveCategorySeo(payload);

      if (response.success) {
        toast.success(response.msg || 'Category SEO saved successfully');
        onSaved?.(response.data);
        onClose();
      } else {
        toast.error(response.msg || response.error || 'Failed to save category SEO');
      }
    } catch (error) {
      console.error('Category SEO save error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!previewUrl) return;

    try {
      await navigator.clipboard.writeText(previewUrl);
      toast.success('Category URL copied to clipboard');
    } catch (error) {
      console.error('Copy URL error:', error);
      toast.error('Failed to copy URL');
    }
  };

  const entityLabel = categoryData?.type === 'subcategory' ? 'Subcategory' : 'Category';
  const entityName = categoryData?.title || item?.title || entityLabel;

  return (
    <div className="seo-modal-overlay" onClick={onClose}>
      <div className="seo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="seo-modal-header">
          <div className="seo-modal-header-left">
            <h3>Edit SEO — {entityLabel}: {entityName}</h3>
            {!loading && !loadError && formData && (
              <div className="seo-modal-score-preview">
                <span className={`seo-status-badge ${statusClass}`}>{statusForDisplay}</span>
                <span className="seo-modal-score-text">{liveScore}/100</span>
              </div>
            )}
          </div>
          <div className="seo-modal-header-actions">
            {!loading && !loadError && previewUrl && (
              <button
                type="button"
                className="seo-modal-copy"
                onClick={handleCopyUrl}
                title="Copy category URL"
              >
                <Copy size={18} />
              </button>
            )}
            <button type="button" className="seo-modal-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {loading ? (
          <div className="seo-modal-loading">
            <div className="seo-loading-spinner" />
            <p>Loading SEO data...</p>
          </div>
        ) : loadError ? (
          <div className="seo-modal-error">
            <p>{loadError}</p>
            <button type="button" className="seo-retry-button" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="seo-modal-tabs">
              <button
                type="button"
                className={`seo-tab ${activeTab === 'basic' ? 'seo-tab-active' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                Basic SEO
              </button>
              <button
                type="button"
                className={`seo-tab ${activeTab === 'sections' ? 'seo-tab-active' : ''}`}
                onClick={() => setActiveTab('sections')}
              >
                Content Sections
              </button>
              <button
                type="button"
                className={`seo-tab ${activeTab === 'advanced' ? 'seo-tab-active' : ''}`}
                onClick={() => setActiveTab('advanced')}
              >
                Advanced SEO
              </button>
            </div>

            <div className="seo-modal-content">
              {activeTab === 'basic' && (
                <div className="seo-basic-grid">
                  <div className="seo-basic-left">
                    <div className="seo-form-group">
                      <label>Page Title</label>
                      <input
                        type="text"
                        className="seo-form-input"
                        value={formData.pageTitle}
                        onChange={(e) => handleFieldChange('pageTitle', e.target.value)}
                        disabled={!canEdit}
                      />
                    </div>

                    <div className="seo-form-group">
                      <label>Meta Title</label>
                      <input
                        type="text"
                        className="seo-form-input"
                        value={formData.metaTitle}
                        maxLength={META_TITLE_MAX}
                        onChange={(e) => handleFieldChange('metaTitle', e.target.value)}
                        disabled={!canEdit}
                      />
                      <div className="seo-char-counter">
                        {formData.metaTitle.length}/{META_TITLE_MAX} characters
                      </div>
                    </div>

                    <div className="seo-form-group">
                      <label>Meta Description</label>
                      <textarea
                        className="seo-form-textarea"
                        value={formData.metaDescription}
                        maxLength={META_DESCRIPTION_MAX}
                        rows={4}
                        onChange={(e) => handleFieldChange('metaDescription', e.target.value)}
                        disabled={!canEdit}
                      />
                      <div className="seo-char-counter">
                        {formData.metaDescription.length}/{META_DESCRIPTION_MAX} characters
                      </div>
                    </div>

                    <div className="seo-form-group">
                      <label>URL Slug</label>
                      <div className="seo-slug-input">
                        <span className="seo-slug-prefix">
                          {categoryData?.type === 'subcategory'
                            ? `order/${slugifyCategory(categoryData?.parentCategory || 'category')}/`
                            : 'order/'}
                        </span>
                        <input
                          type="text"
                          className="seo-slug-field"
                          value={formData.urlSlug}
                          onChange={(e) => handleFieldChange('urlSlug', e.target.value)}
                          placeholder="category-slug"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="seo-basic-right">
                    <div className="seo-form-group">
                      <label>Page Description</label>
                      <textarea
                        className="seo-form-textarea seo-description-textarea"
                        value={formData.pageDescription}
                        rows={8}
                        onChange={(e) => handleFieldChange('pageDescription', e.target.value)}
                        disabled={!canEdit}
                      />
                    </div>

                    <div className="seo-form-group">
                      <label>Google Search Preview</label>
                      <div className="seo-google-preview">
                        <div className="seo-preview-title">
                          {formData.metaTitle || formData.pageTitle || entityName}
                        </div>
                        <div className="seo-preview-url">{previewUrl}</div>
                        <div className="seo-preview-description">
                          {formData.metaDescription || 'Your meta description will appear here.'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'sections' && (
                <div className="seo-sections-panel">
                  <p className="seo-field-hint">
                    These sections appear on the category page just above the footer.
                  </p>
                  {formData.contentSections.length === 0 && (
                    <p className="seo-field-hint">No content sections added yet.</p>
                  )}
                  {formData.contentSections.map((section, index) => (
                    <div key={`section-${index}`} className="seo-content-section-card">
                      <div className="seo-content-section-header">
                        <h4>Section {index + 1}</h4>
                        {canEdit && (
                          <button
                            type="button"
                            className="seo-section-remove-btn"
                            onClick={() => handleRemoveSection(index)}
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="seo-form-group">
                        <label>Title</label>
                        <input
                          type="text"
                          className="seo-form-input"
                          value={section.title}
                          onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                          disabled={!canEdit}
                        />
                      </div>
                      <div className="seo-form-group">
                        <label>Paragraph</label>
                        <textarea
                          className="seo-form-textarea"
                          rows={5}
                          value={section.paragraph}
                          onChange={(e) => handleSectionChange(index, 'paragraph', e.target.value)}
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  ))}
                  {canEdit && (
                    <button type="button" className="seo-add-section-btn" onClick={handleAddSection}>
                      <Plus size={16} />
                      Add Title &amp; Paragraph
                    </button>
                  )}
                </div>
              )}

              {activeTab === 'advanced' && (
                <div className="seo-advanced-grid">
                  <div className="seo-advanced-card">
                    <h4>Category Image Alt Text</h4>
                    {formData.image ? (
                      <div className="seo-image-alt-list">
                        <div className="seo-image-alt-item">
                          <div className="seo-image-alt-thumb">
                            <img
                              src={formData.image}
                              alt={formData.imageAlt || entityName}
                            />
                          </div>
                          <div className="seo-image-alt-field">
                            <label>Image Alt Text</label>
                            <input
                              type="text"
                              className="seo-form-input"
                              placeholder="Describe this image for accessibility..."
                              value={formData.imageAlt}
                              onChange={(e) => handleFieldChange('imageAlt', e.target.value)}
                              disabled={!canEdit}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="seo-field-hint">No category image uploaded. You can still set alt text for when an image is added.</p>
                        <div className="seo-form-group">
                          <label>Image Alt Text</label>
                          <input
                            type="text"
                            className="seo-form-input"
                            placeholder="Describe this image for accessibility..."
                            value={formData.imageAlt}
                            onChange={(e) => handleFieldChange('imageAlt', e.target.value)}
                            disabled={!canEdit}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="seo-advanced-card">
                    <h4>Category Schema Markup</h4>
                    <textarea
                      className="seo-schema-textarea"
                      value={formData.categorySchemaMarkup}
                      onChange={(e) => handleFieldChange('categorySchemaMarkup', e.target.value)}
                      spellCheck={false}
                      disabled={!canEdit}
                    />
                  </div>

                  <div className="seo-advanced-card">
                    <h4>Breadcrumbs Schema (JSON-LD)</h4>
                    <textarea
                      className="seo-schema-textarea"
                      value={formData.breadCrumbSchema}
                      onChange={(e) => handleFieldChange('breadCrumbSchema', e.target.value)}
                      spellCheck={false}
                      disabled={!canEdit}
                    />
                  </div>

                  <div className="seo-advanced-card">
                    <h4>FAQ Schema (JSON-LD)</h4>
                    <textarea
                      className="seo-schema-textarea"
                      value={formData.faqSchema}
                      onChange={(e) => handleFieldChange('faqSchema', e.target.value)}
                      spellCheck={false}
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="seo-modal-footer">
              <button type="button" className="seo-cancel-button" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              {canEdit && (
                <button
                  type="button"
                  className="seo-save-button seo-save-button-active"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EditCategorySeoModal;
